/**
 * BulkCSVUpload Component
 *
 * UI/Aesthetics: Complex CSV upload interface with file dropzone, data preview table, upload progress tracking
 * Functions:
 * - CSV parsing with proper quote handling and validation
 * - Editable data table for reviewing/correcting data before upload
 * - Batch upload processing with individual row status tracking
 * - Progress indicators and error handling
 * - Retry functionality for failed uploads
 * - Upload statistics and summary display
 */

import React, { useState, useCallback, useMemo } from 'react';
import { T, Var } from 'gt-react';
import {
  AlertContainer as Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  CheckCircleIcon,
  WarningCircleIcon,
  ClockIcon,
  UploadIcon,
  InfoIcon,
  DownloadIcon,
} from '@phosphor-icons/react';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import EditableCSVTable from './EditableCSVTable';
import {
  downloadCSVFile,
  generateGenericCSVTemplate,
} from 'src/utils/csvTemplates';

export interface BulkCSVUploadProps {
  onUploadRow: (row: Record<string, unknown>, index: number) => Promise<void>;
  expectedColumns: string[];
  onComplete?: (results: UploadResult[]) => void;
  title?: string;
  description?: string;
  maxRows?: number;
  disabled?: boolean;
}

export interface UploadResult {
  index: number;
  status: 'pending' | 'uploading' | 'success' | 'failed';
  error?: string;
  data: Record<string, unknown>;
}

interface ParsedCSVData {
  headers: string[];
  rows: Record<string, unknown>[];
  errors: string[];
}

const BulkCSVUpload: React.FC<BulkCSVUploadProps> = ({
  onUploadRow,
  expectedColumns,
  onComplete,
  title = 'Bulk CSV Upload',
  description = 'Upload a CSV file to import multiple records at once.',
  maxRows = 1000,
  disabled = false,
}) => {
  const [csvData, setCsvData] = useState<ParsedCSVData | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, unknown>[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  // Parse CSV line with proper quote handling
  const parseCSVLine = useCallback((line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        // Handle escaped quotes (double quotes)
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }, []);

  // Parse CSV file
  const parseCSV = useCallback(
    (file: File): Promise<ParsedCSVData> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const csvText = e.target?.result as string;
            const lines = csvText.split('\n').filter((line) => line.trim());

            if (lines.length === 0) {
              reject(new Error('CSV file is empty'));
              return;
            }

            // Parse headers
            const headers = parseCSVLine(lines[0]).map((h) =>
              h.replace(/^"|"$/g, ''),
            );

            // Validate expected columns (case-insensitive)
            const errors: string[] = [];
            const headersLowerCase = headers.map((h) => h.toLowerCase());
            const missingColumns = expectedColumns.filter(
              (col) => !headersLowerCase.includes(col.toLowerCase()),
            );
            if (missingColumns.length > 0) {
              errors.push(
                `Missing required columns: ${missingColumns.join(', ')}`,
              );
            }

            // Create normalized header mapping (case-insensitive)
            const headerMapping: Record<string, string> = {};
            headers.forEach((header) => {
              const expectedColumn = expectedColumns.find(
                (col) => col.toLowerCase() === header.toLowerCase(),
              );
              headerMapping[header] = expectedColumn || header;
            });

            // Parse data rows
            const rows: Record<string, unknown>[] = [];
            for (let i = 1; i < lines.length && rows.length < maxRows; i++) {
              const values = parseCSVLine(lines[i]).map((v) =>
                v.replace(/^"|"$/g, ''),
              );
              if (values.length !== headers.length) {
                errors.push(
                  `Row ${i}: Column count mismatch (expected ${headers.length}, got ${values.length})`,
                );
                continue;
              }

              const row: Record<string, unknown> = {};
              headers.forEach((header, index) => {
                const normalizedHeader = headerMapping[header];
                row[normalizedHeader] = values[index] || '';
              });
              rows.push(row);
            }

            if (lines.length > maxRows + 1) {
              errors.push(
                `File contains ${
                  lines.length - 1
                } rows, but only first ${maxRows} will be processed`,
              );
            }

            // Return normalized headers to match the row data keys
            const normalizedHeaders = headers.map(
              (header) => headerMapping[header],
            );
            resolve({ headers: normalizedHeaders, rows, errors });
          } catch (error) {
            reject(new Error(`Failed to parse CSV: ${error}`));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },
    [expectedColumns, maxRows, parseCSVLine],
  );

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      setFileError(null); // Clear previous errors

      const csvFile = files.find((f) => f.name.toLowerCase().endsWith('.csv'));
      if (!csvFile) {
        setFileError('Please select a CSV file');
        return;
      }

      try {
        const parsed = await parseCSV(csvFile);
        setCsvData(parsed);
        setEditedData(parsed.rows);

        // Initialize upload results
        const results: UploadResult[] = parsed.rows.map((row, index) => ({
          index,
          status: 'pending' as const,
          data: row,
        }));
        setUploadResults(results);
      } catch (error) {
        setFileError(`Error parsing CSV: ${error}`);
      }
    },
    [parseCSV],
  );

  const handleDataEdit = useCallback(
    (rowIndex: number, field: string, value: unknown) => {
      setEditedData((prev) => {
        const updated = [...prev];
        updated[rowIndex] = { ...updated[rowIndex], [field]: value };
        return updated;
      });

      setUploadResults((prev) => {
        const updated = [...prev];
        updated[rowIndex] = {
          ...updated[rowIndex],
          data: { ...updated[rowIndex].data, [field]: value },
          status: 'pending', // Reset status when data is edited
        };
        return updated;
      });
    },
    [],
  );

  const handleUploadAll = useCallback(async () => {
    if (!csvData || isUploading) return;

    setIsUploading(true);
    const results = [...uploadResults];

    for (let i = 0; i < results.length; i++) {
      if (results[i].status === 'success') continue; // Skip already successful uploads

      // Update status to uploading
      results[i] = { ...results[i], status: 'uploading' };
      setUploadResults([...results]);

      try {
        await onUploadRow(editedData[i], i);
        results[i] = { ...results[i], status: 'success', error: undefined };
      } catch (error) {
        results[i] = {
          ...results[i],
          status: 'failed',
          error: error instanceof Error ? error.message : 'Upload failed',
        };
      }

      setUploadResults([...results]);
    }

    setIsUploading(false);
    if (onComplete) {
      onComplete(results);
    }
  }, [
    csvData,
    uploadResults,
    editedData,
    onUploadRow,
    onComplete,
    isUploading,
  ]);

  const handleRetryRow = useCallback(
    async (index: number) => {
      if (isUploading) return;

      const results = [...uploadResults];
      results[index] = { ...results[index], status: 'uploading' };
      setUploadResults(results);

      try {
        await onUploadRow(editedData[index], index);
        results[index] = {
          ...results[index],
          status: 'success',
          error: undefined,
        };
      } catch (error) {
        results[index] = {
          ...results[index],
          status: 'failed',
          error: error instanceof Error ? error.message : 'Upload failed',
        };
      }

      setUploadResults([...results]);
    },
    [uploadResults, editedData, onUploadRow, isUploading],
  );

  const handleDownloadTemplate = useCallback(() => {
    // Generate generic CSV template content
    const csvContent = generateGenericCSVTemplate(expectedColumns);

    // Download using shared utility
    downloadCSVFile(csvContent, 'template.csv');
  }, [expectedColumns]);

  const handleClear = useCallback(() => {
    setCsvData(null);
    setUploadResults([]);
    setEditedData([]);
  }, []);

  const uploadStats = useMemo(() => {
    const total = uploadResults.length;
    const success = uploadResults.filter((r) => r.status === 'success').length;
    const failed = uploadResults.filter((r) => r.status === 'failed').length;
    const pending = uploadResults.filter((r) => r.status === 'pending').length;
    const uploading = uploadResults.filter(
      (r) => r.status === 'uploading',
    ).length;

    return { total, success, failed, pending, uploading };
  }, [uploadResults]);

  const canUpload =
    csvData &&
    !isUploading &&
    uploadStats.pending > 0 &&
    csvData.errors.length === 0;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {!csvData ? (
        <div className="space-y-4">
          <FileUploadDropzone
            onFilesSelected={handleFilesSelected}
            uploadProgress={[]}
            isUploading={false}
            disabled={disabled}
            maxFiles={1}
          />

          {fileError && (
            <Alert className="border-destructive bg-destructive/10">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertDescription>{fileError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                <T>
                  <strong>Expected columns:</strong>{' '}
                  <Var>{expectedColumns.join(', ')}</Var>
                </T>
              </AlertDescription>
            </Alert>

            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                disabled={disabled}
                className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
              >
                <DownloadIcon className="w-4 h-4 mr-2" />
                <T>Download Template CSV</T>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Upload Summary */}
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  <T>Upload Progress</T>
                </h3>
                <div className="flex gap-2">
                  <Badge
                    variant="outline"
                    className="text-green-700 border-green-200 bg-green-50"
                  >
                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                    <T>
                      <Var>{uploadStats.success}</Var> Success
                    </T>
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-red-700 border-red-200 bg-red-50"
                  >
                    <WarningCircleIcon className="w-3 h-3 mr-1" />
                    <T>
                      <Var>{uploadStats.failed}</Var> Failed
                    </T>
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-gray-700 border-gray-200 bg-gray-50"
                  >
                    <ClockIcon className="w-3 h-3 mr-1" />
                    <T>
                      <Var>{uploadStats.pending}</Var> Pending
                    </T>
                  </Badge>
                </div>
              </div>

              {isUploading && (
                <div className="mb-4">
                  <Progress
                    value={
                      ((uploadStats.success + uploadStats.failed) /
                        uploadStats.total) *
                      100
                    }
                    className="h-2"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleUploadAll}
                  disabled={!canUpload}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <UploadIcon className="w-4 h-4 mr-2" />
                  {isUploading ? (
                    <T>Uploading...</T>
                  ) : (
                    <T>
                      Upload <Var>{uploadStats.pending}</Var> Rows
                    </T>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={isUploading}
                >
                  <T>Clear & Start Over</T>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Validation Errors */}
          {csvData.errors.length > 0 && (
            <div className="space-y-2">
              {csvData.errors.map((error, index) => (
                <Alert key={index} className="border-yellow-200 bg-yellow-50">
                  <WarningCircleIcon className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    {error}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          {/* Data Table */}
          <EditableCSVTable
            data={editedData}
            columns={csvData.headers}
            uploadResults={uploadResults}
            onDataEdit={handleDataEdit}
            onRetryRow={handleRetryRow}
            isUploading={isUploading}
          />
        </div>
      )}
    </div>
  );
};

export default BulkCSVUpload;
